import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  GlobalStyle,
  Page,
  Container,
  Hero,
  Eyebrow,
  HeroTitle,
  HeroMeta,
  HeroLede,
  AcceptBanner,
  Layout,
  TocWrap,
  TocTitle,
  TocList,
  TocLink,
  Content,
  Section,
  SectionTitle,
  SubTitle,
  P,
  List,
  ListItem,
  Callout,
  TableWrap,
  Table,
  ContactList,
  ContactRow,
  BackHome,
} from "./infoPageStyles";

const TOC = [
  { id: "overview", label: "1. Overview" },
  { id: "pricing", label: "2. What NudR Costs" },
  { id: "trial", label: "3. Free Trial" },
  { id: "renewal", label: "4. Automatic Renewal" },
  { id: "cancel", label: "5. How to Cancel" },
  { id: "refunds", label: "6. Refunds" },
  { id: "failed", label: "7. Failed Payments" },
  { id: "merch", label: "8. Merchandise Orders" },
  { id: "disputes", label: "9. Billing Questions" },
  { id: "contact", label: "10. Contact Us" },
];

export default function RefundsPage() {
  const [active, setActive] = useState(TOC[0].id);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-15% 0px -70% 0px" },
    );
    TOC.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <Page>
      <GlobalStyle />

      <Hero>
        <Container>
          <Eyebrow>Billing</Eyebrow>
          <HeroTitle>Refunds &amp; Cancellation</HeroTitle>
          <HeroMeta>
            <span>Effective Date: August 11, 2026</span>
          </HeroMeta>
          <HeroLede>
            This page explains what NudR costs, how billing and automatic
            renewal work, how to cancel, and when refunds are and aren't
            available. It supplements Section 3 of our{" "}
            <Link to="/terms">Terms of Service</Link>.
          </HeroLede>
          <AcceptBanner>
            NudR membership is billed by NUDR INC and processed by Stripe.
            Subscriptions renew automatically until you cancel. You can cancel
            at any time and keep access through the end of the period you've
            already paid for.
          </AcceptBanner>
        </Container>
      </Hero>

      <Container>
        <Layout>
          <TocWrap>
            <TocTitle>On this page</TocTitle>
            <TocList>
              {TOC.map((item) => (
                <li key={item.id}>
                  <TocLink href={`#${item.id}`} $active={active === item.id}>
                    {item.label}
                  </TocLink>
                </li>
              ))}
            </TocList>
          </TocWrap>

          <Content>
            <Section id="overview">
              <SectionTitle>
                <span>01</span>Overview
              </SectionTitle>
              <P>
                NudR sells a <strong>membership subscription</strong>, which is
                required to use the app, and occasionally{" "}
                <strong>physical merchandise</strong>. Different rules apply to
                each — subscriptions in Sections 2 to 7, merchandise in Section
                8.
              </P>
              <P>
                All prices are in <strong>US Dollars (USD)</strong>. Payments
                are processed by Stripe; NudR does not store your full card
                number.
              </P>
            </Section>

            <Section id="pricing">
              <SectionTitle>
                <span>02</span>What NudR Costs
              </SectionTitle>
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>Plan</th>
                      <th>Price (USD)</th>
                      <th>Billed</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Monthly</td>
                      <td>$9.99 per month</td>
                      <td>Every month</td>
                    </tr>
                    <tr>
                      <td>Annual</td>
                      <td>$99.99 per year</td>
                      <td>Once a year — works out at $8.33 per month</td>
                    </tr>
                  </tbody>
                </Table>
              </TableWrap>
              <P>
                Membership is required to access NudR. We may change prices; if
                we do, we'll give you notice before it affects your renewal, and
                you can cancel before the new price takes effect. Current
                pricing is always on our <Link to="/pricing">Pricing page</Link>
                .
              </P>
            </Section>

            <Section id="trial">
              <SectionTitle>
                <span>03</span>Free Trial
              </SectionTitle>
              <P>
                New members get a <strong>7-day free trial</strong>. You aren't
                charged during the trial.
              </P>
              <List>
                <ListItem>
                  Cancel before the trial ends and you pay nothing.
                </ListItem>
                <ListItem>
                  If you don't cancel, your chosen plan starts automatically
                  when the trial ends and your payment method is charged.
                </ListItem>
                <ListItem>
                  The trial is available once per person. We may decline to
                  offer one where we detect repeat signups.
                </ListItem>
              </List>
            </Section>

            <Section id="renewal">
              <SectionTitle>
                <span>04</span>Automatic Renewal
              </SectionTitle>
              <Callout>
                <P>
                  <strong>
                    Your subscription renews automatically until you cancel it.
                  </strong>{" "}
                  Monthly plans renew every month, annual plans every year, at
                  the price in effect at renewal. We charge the payment method
                  on file on your renewal date.
                </P>
              </Callout>
              <P>
                Some US states require us to send a reminder before an automatic
                renewal, or to let you cancel by the same method you signed up
                with. Where those laws apply to you we follow them, and nothing
                on this page limits any right they give you.
              </P>
            </Section>

            <Section id="cancel">
              <SectionTitle>
                <span>05</span>How to Cancel
              </SectionTitle>
              <P>You can cancel at any time:</P>
              <List>
                <ListItem>
                  <strong>In the app</strong> — Settings, then Manage
                  Subscription.
                </ListItem>
                <ListItem>
                  <strong>By email</strong> — write to{" "}
                  <a href="mailto:support@nudr.app">support@nudr.app</a> from
                  the address on your account and we'll cancel it for you.
                </ListItem>
              </List>
              <P>
                Cancellation takes effect at the end of your current billing
                period. You keep access until then and won't be charged again.
                We don't pro-rate part-months or part-years.
              </P>
              <P>
                Cancelling is not the same as deleting your account. To remove
                your data, see{" "}
                <Link to="/delete-account">Delete Your Account</Link>.
              </P>
            </Section>

            <Section id="refunds">
              <SectionTitle>
                <span>06</span>Refunds
              </SectionTitle>
              <P>
                <strong>
                  Subscription payments are generally non-refundable
                </strong>{" "}
                — including for partial billing periods, unused time, or
                features you didn't use. This applies whether you cancel,
                downgrade, or have your account terminated for breaching our{" "}
                <Link to="/terms">Terms</Link> or{" "}
                <Link to="/guidelines">Community Guidelines</Link>.
              </P>
              <SubTitle>When we will refund you</SubTitle>
              <P>Despite the above, we'll issue a refund where:</P>
              <List>
                <ListItem>
                  <strong>You were charged in error</strong> — for example,
                  double-billed, or charged after cancelling.
                </ListItem>
                <ListItem>
                  <strong>You were charged during a free trial</strong> you had
                  already cancelled.
                </ListItem>
                <ListItem>
                  <strong>A sustained technical fault</strong> stopped you using
                  the membership you paid for.
                </ListItem>
                <ListItem>
                  <strong>Consumer law where you live requires it.</strong> Some
                  places give you statutory cancellation or refund rights that
                  override this policy. We honour those rights — nothing here
                  removes them.
                </ListItem>
              </List>
              <SubTitle>How to request one</SubTitle>
              <P>
                Email <a href="mailto:support@nudr.app">support@nudr.app</a>{" "}
                from your account email within <strong>30 days</strong> of the
                charge, with the date and amount. We aim to respond within 5
                business days. Approved refunds go back to your original payment
                method and typically take 5–10 business days to appear.
              </P>
            </Section>

            <Section id="failed">
              <SectionTitle>
                <span>07</span>Failed Payments
              </SectionTitle>
              <P>
                If a renewal payment fails, we'll mark your subscription past
                due and give you a short grace period to update your payment
                method before paid access is suspended. We may retry the charge.
                If payment keeps failing, the subscription is cancelled.
              </P>
            </Section>

            <Section id="merch">
              <SectionTitle>
                <span>08</span>Merchandise Orders
              </SectionTitle>
              <P>
                We occasionally sell physical merchandise, such as the NudR
                T-shirt. These terms apply to those orders.
              </P>
              <SubTitle>Pricing &amp; shipping</SubTitle>
              <P>
                Item prices are shown in USD on the product page. Shipping
                costs, available destinations, and estimated delivery times are
                calculated and displayed{" "}
                <strong>at checkout before you pay</strong>. Any customs duties
                or import taxes on international orders are your
                responsibility.
              </P>
              <Callout>
                <P>
                  <strong>Preorders</strong> are charged when you order and ship
                  once stock is available. We'll email you tracking as soon as
                  your order is dispatched. If a preorder is delayed
                  significantly beyond the window shown at checkout, you can
                  cancel it for a full refund.
                </P>
              </Callout>
              <SubTitle>Returns</SubTitle>
              <P>
                <strong>
                  You can return unworn merchandise within 30 days of delivery
                </strong>{" "}
                for a refund of the item price. Items must be unworn, unwashed,
                and in original condition with any tags attached.
              </P>
              <List>
                <ListItem>
                  <strong>Faulty or incorrect items:</strong> we cover return
                  shipping and refund you in full, including original shipping.
                  Email us a photo and we'll sort it.
                </ListItem>
                <ListItem>
                  <strong>Change of mind:</strong> you cover return shipping and
                  we refund the item price.
                </ListItem>
                <ListItem>
                  <strong>Exchanges:</strong> contact us for a different size
                  and we'll arrange it subject to stock.
                </ListItem>
              </List>
              <P>
                To start a return, email{" "}
                <a href="mailto:support@nudr.app">support@nudr.app</a> with your
                order number. Don't post anything back before contacting us —
                we'll send the return address and instructions. Refunds are
                processed within 10 business days of us receiving the item.
              </P>
            </Section>

            <Section id="disputes">
              <SectionTitle>
                <span>09</span>Billing Questions
              </SectionTitle>
              <P>
                If you see a charge you don't recognise or think is wrong,{" "}
                <strong>please contact us first</strong> at{" "}
                <a href="mailto:support@nudr.app">support@nudr.app</a>. We can
                usually resolve billing issues quickly and directly, which is
                faster than raising a dispute with your bank.
              </P>
              <P>
                If we can't resolve it between us, the dispute resolution and
                arbitration provisions in Section 16 of our{" "}
                <Link to="/terms">Terms of Service</Link> apply.
              </P>
            </Section>

            <Section id="contact">
              <SectionTitle>
                <span>10</span>Contact Us
              </SectionTitle>
              <ContactList>
                <ContactRow>
                  <b>Billing &amp; support:</b>{" "}
                  <a href="mailto:support@nudr.app">support@nudr.app</a>
                </ContactRow>
                <ContactRow>
                  <b>Legal:</b>{" "}
                  <a href="mailto:legal@nudr.app">legal@nudr.app</a>
                </ContactRow>
                <ContactRow>
                  <b>Registered Address:</b> NUDR INC, 1105 N Buena Vista
                  Street, Burbank, CA 91505, U.S.A.
                </ContactRow>
              </ContactList>

              <P style={{ marginTop: "1.4rem", fontSize: "0.85rem" }}>
                © {new Date().getFullYear()} NudR. All rights reserved.
              </P>

              <BackHome to="/">← Back to home</BackHome>
            </Section>
          </Content>
        </Layout>
      </Container>
    </Page>
  );
}
